import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { FaSearch, FaTimes } from 'react-icons/fa';
import { MdSportsCricket } from 'react-icons/md';
import { IoMdFootball } from 'react-icons/io';
import { GiTennisBall } from 'react-icons/gi';
import {
  selectCricketInPlayGames,
  selectTennisInPlayGames,
  selectSoccerInPlayGames,
} from '../../redux/selectors';

const SPORT_CONFIG = {
  cricket: { icon: MdSportsCricket, color: '#2e7d32', label: 'CRICKET' },
  soccer: { icon: IoMdFootball, color: '#1565c0', label: 'FOOTBALL' },
  tennis: { icon: GiTennisBall, color: '#f9a825', label: 'TENNIS' },
};

const getOneLiveMatch = (games) => {
  if (!Array.isArray(games) || games.length === 0) return null;
  const live = games.find((g) => g.inPlay === true || g.inPlay === 'true');
  return live || games[0];
};

const MobileLiveBar = () => {
  const navigate = useNavigate();
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [resultsOpen, setResultsOpen] = useState(false);
  const inputRef = useRef(null);
  const barRef = useRef(null);

  const cricketGames = useSelector(selectCricketInPlayGames);
  const soccerGames = useSelector(selectSoccerInPlayGames);
  const tennisGames = useSelector(selectTennisInPlayGames);

  const liveMatches = useMemo(() => {
    const matches = [];
    const cricket = getOneLiveMatch(cricketGames);
    if (cricket) matches.push({ ...cricket, sport: 'cricket' });
    const soccer = getOneLiveMatch(soccerGames);
    if (soccer) matches.push({ ...soccer, sport: 'soccer' });
    const tennis = getOneLiveMatch(tennisGames);
    if (tennis) matches.push({ ...tennis, sport: 'tennis' });
    return matches;
  }, [cricketGames, soccerGames, tennisGames]);

  const allMatches = useMemo(() => [
    ...(Array.isArray(cricketGames) ? cricketGames.map((g) => ({ ...g, sport: 'cricket' })) : []),
    ...(Array.isArray(soccerGames) ? soccerGames.map((g) => ({ ...g, sport: 'soccer' })) : []),
    ...(Array.isArray(tennisGames) ? tennisGames.map((g) => ({ ...g, sport: 'tennis' })) : []),
  ], [cricketGames, soccerGames, tennisGames]);

  const filtered = useMemo(() => {
    if (query.length < 2) return [];
    const q = query.toLowerCase();
    return allMatches.filter((m) => m.eventName?.toLowerCase().includes(q));
  }, [query, allMatches]);

  const handleSearchToggle = useCallback(() => {
    if (searchOpen) {
      setSearchOpen(false);
      setQuery('');
      setResultsOpen(false);
    } else {
      setSearchOpen(true);
    }
  }, [searchOpen]);

  useEffect(() => {
    if (searchOpen && inputRef.current) {
      const timer = setTimeout(() => inputRef.current.focus(), 300);
      return () => clearTimeout(timer);
    }
  }, [searchOpen]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (barRef.current && !barRef.current.contains(e.target)) {
        setSearchOpen(false);
        setQuery('');
        setResultsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleChange = (e) => {
    const value = e.target.value;
    setQuery(value);
    setResultsOpen(value.length >= 2);
  };

  const handleResultClick = (match) => {
    setSearchOpen(false);
    setQuery('');
    setResultsOpen(false);
    const sport = match.sport || 'cricket';
    const id = match.cricketId || match.soccerId || match.tennisId || match.gameId;
    navigate(`/game/${sport}/${id}`);
  };

  const handleMatchCardClick = (match) => {
    const sport = match.sport || 'cricket';
    const id = match.cricketId || match.soccerId || match.tennisId || match.gameId;
    navigate(`/game/${sport}/${id}`);
  };

  const cfg = (sport) => SPORT_CONFIG[sport] || SPORT_CONFIG.cricket;

  return (
    <div className="mobile-live-bar" ref={barRef}>
      {/* Search toggle icon */}
      <button
        className="mobile-live-bar__search-btn"
        onClick={handleSearchToggle}
        type="button"
        aria-label={searchOpen ? 'Close search' : 'Open search'}
      >
        {searchOpen ? <FaTimes /> : <FaSearch />}
      </button>

      {/* Search input — always in DOM, width transitions via CSS */}
      <div className={`mobile-live-bar__search ${searchOpen ? 'mobile-live-bar__search--open' : ''}`}>
        <input
          ref={inputRef}
          type="text"
          className="mobile-live-bar__search-input"
          placeholder="Search Events..."
          value={query}
          onChange={handleChange}
          tabIndex={searchOpen ? 0 : -1}
          aria-label="Search events"
        />
      </div>

      {/* Dropdown rendered outside the overflow:hidden search container */}
      {searchOpen && resultsOpen && filtered.length > 0 && (
        <div className="mobile-live-bar__dropdown">
          <div className="header-search__dropdown-body">
            {filtered.map((match, i) => (
              <button
                key={match.gameId || match.cricketId || i}
                className="header-search__result-item"
                onClick={() => handleResultClick(match)}
                type="button"
              >
                <span className="header-search__result-name">
                  {match.eventName}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Live match ticker — always visible */}
      <div className="mobile-live-bar__ticker">
        {liveMatches.length > 0 ? (
          liveMatches.map((match) => {
            const SportIcon = cfg(match.sport).icon;
            return (
              <button
                key={match.gameId || match.cricketId || match.soccerId || match.tennisId}
                className="mobile-live-bar__card"
                onClick={() => handleMatchCardClick(match)}
                type="button"
                aria-label={`Go to ${match.eventName}`}
              >
                <span
                  className="mobile-live-bar__card-icon"
                  style={{ color: cfg(match.sport).color }}
                >
                  <SportIcon />
                </span>
                <span className="mobile-live-bar__card-name">
                  {match.eventName}
                </span>
                <span className="mobile-live-bar__card-live">LIVE</span>
              </button>
            );
          })
        ) : (
          <span className="mobile-live-bar__empty">No live matches</span>
        )}
      </div>
    </div>
  );
};

export default MobileLiveBar;
